import { type ReactNode, createContext, useCallback, useContext } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  type AdditionalInfoRequest,
  type CertificateRequest,
  type CvResponse,
  type CvUpdateRequest,
  type EducationRequest,
  type ForeignLanguageRequest,
  type PersonalInformationRequest,
  type WorkExperienceRequest,
} from '@/services/api/codegen/api';

import {
  useGetCandidateProfileInfo,
  useUpdateCandidateProfileInfo,
} from './queries';

interface AccountDataContextValue {
  data?: CvResponse;
  isLoading: boolean;
  isSubmitting: boolean;
  error: Error | null;

  updatePersonalInfo: (data: PersonalInformationRequest) => Promise<void>;
  updateWorkExperience: (data: Array<WorkExperienceRequest>) => Promise<void>;
  updateEducation: (data: Array<EducationRequest>) => Promise<void>;
  updateForeignLanguage: (data: Array<ForeignLanguageRequest>) => Promise<void>;
  updateCertificate: (data: Array<CertificateRequest>) => Promise<void>;
  updateAdditionalInfo: (data: Array<AdditionalInfoRequest>) => Promise<void>;

  updateFullProfile: (data: CvUpdateRequest) => Promise<void>;
}

const AccountDataContext = createContext<AccountDataContextValue | undefined>(
  undefined
);

export const useAccountData = () => {
  const context = useContext(AccountDataContext);
  if (!context) {
    throw new Error('useAccountData must be used within AccountDataProvider');
  }
  return context;
};

interface AccountDataProviderProps {
  children: ReactNode;
}

export const AccountDataProvider: React.FC<AccountDataProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useGetCandidateProfileInfo();

  const updateCandidateProfileInfoMutation = useUpdateCandidateProfileInfo({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getCandidateProfileCv'],
      });
    },
  });

  const updateSection = useCallback(
    async (updates: Partial<CvUpdateRequest>) => {
      await updateCandidateProfileInfoMutation.mutateAsync({ body: updates });
    },
    [updateCandidateProfileInfoMutation]
  );

  const updatePersonalInfo = useCallback(
    async (data: PersonalInformationRequest) => {
      await updateSection({ personalInfo: data });
    },
    [updateSection]
  );

  const updateWorkExperience = useCallback(
    async (data: Array<WorkExperienceRequest>) => {
      await updateSection({ workExperience: data });
    },
    [updateSection]
  );

  const updateEducation = useCallback(
    async (data: Array<EducationRequest>) => {
      await updateSection({ education: data });
    },
    [updateSection]
  );

  const updateForeignLanguage = useCallback(
    async (data: Array<ForeignLanguageRequest>) => {
      await updateSection({ foreignLanguage: data });
    },
    [updateSection]
  );

  const updateCertificate = useCallback(
    async (data: Array<CertificateRequest>) => {
      await updateSection({ certificate: data });
    },
    [updateSection]
  );

  const updateAdditionalInfo = useCallback(
    async (data: Array<AdditionalInfoRequest>) => {
      await updateSection({ additionalInfo: data });
    },
    [updateSection]
  );

  const updateFullProfile = useCallback(
    async (data: CvUpdateRequest) => {
      await updateCandidateProfileInfoMutation.mutateAsync({ body: data });
    },
    [updateCandidateProfileInfoMutation]
  );

  const contextValue: AccountDataContextValue = {
    data,
    isLoading,
    isSubmitting: updateCandidateProfileInfoMutation.isPending,
    error,
    updatePersonalInfo,
    updateWorkExperience,
    updateEducation,
    updateForeignLanguage,
    updateCertificate,
    updateAdditionalInfo,
    updateFullProfile,
  };

  return (
    <AccountDataContext.Provider value={contextValue}>
      {children}
    </AccountDataContext.Provider>
  );
};
